import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/lib/i18n";

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const BANNER_KEY = "svj_install_banner_hidden";

const useInstall = () => {
  const [prompt, setPrompt] = useState(() => window.__installPrompt || null);
  const [installed, setInstalled] = useState(isStandalone);
  useEffect(() => {
    const onReady = () => setPrompt(window.__installPrompt);
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("svj-installable", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("svj-installable", onReady);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);
  const ios = isIOS();
  const install = async () => {
    if (!prompt) { return false; }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__installPrompt = null;
    setPrompt(null);
    if (outcome === "accepted") { setInstalled(true); }
    return true;
  };
  return { available: !installed && (!!prompt || ios), ios, install };
};

const IosSteps = ({ open, onClose }) => {
  const { t } = useLang();
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F172A] border-[#D4AF37]/30 text-slate-200" data-testid="install-ios-dialog">
        <DialogHeader><DialogTitle className="gold-text font-display">{t("install.title")}</DialogTitle></DialogHeader>
        <ol className="grid gap-4 text-sm">
          <li className="flex gap-3 items-center"><Share size={20} className="text-[#D4AF37] shrink-0" />{t("install.ios1")}</li>
          <li className="flex gap-3 items-center"><PlusSquare size={20} className="text-[#D4AF37] shrink-0" />{t("install.ios2")}</li>
          <li className="flex gap-3 items-center"><Download size={20} className="text-[#D4AF37] shrink-0" />{t("install.ios3")}</li>
        </ol>
      </DialogContent>
    </Dialog>
  );
};

export const InstallButton = ({ testid = "install-app-button", className = "", solid = false }) => {
  const { t } = useLang();
  const { available, ios, install } = useInstall();
  const [steps, setSteps] = useState(false);
  if (!available) { return null; }
  const onClick = async () => {
    if (!(await install()) && ios) { setSteps(true); }
  };
  return (
    <>
      <button type="button" onClick={onClick} data-testid={testid}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors duration-200 ${solid ? "bg-[#D4AF37] text-[#4a0505] font-semibold hover:bg-[#E8C55A]" : "border border-[#D4AF37]/50 text-[#F3DC8B] hover:bg-[#D4AF37]/10"} ${className}`}>
        <Download size={16} /> {t("install.btn")}
      </button>
      <IosSteps open={steps} onClose={() => setSteps(false)} />
    </>
  );
};

export const InstallBanner = () => {
  const { t } = useLang();
  const { available } = useInstall();
  const [hidden, setHidden] = useState(() => Number(localStorage.getItem(BANNER_KEY) || 0) > Date.now());
  if (!available || hidden) { return null; }
  const close = () => {
    localStorage.setItem(BANNER_KEY, String(Date.now() + 14 * 864e5));
    setHidden(true);
  };
  return (
    <div className="md:hidden fixed bottom-4 left-4 right-24 z-40 rounded-2xl border border-[#D4AF37]/40 bg-[#0F172A] p-4 shadow-[0_10px_40px_rgba(0,0,0,0.5)] fade-up" data-testid="install-banner">
      <button type="button" onClick={close} className="absolute top-2 right-2 text-slate-400" aria-label="Close" data-testid="install-banner-close"><X size={16} /></button>
      <div className="flex items-center gap-3 mb-3 pr-4">
        <img src="/brand/logo-sm.webp" alt="" className="w-10 h-10 rounded-full" />
        <p className="text-sm text-slate-200 leading-snug">{t("install.banner")}</p>
      </div>
      <InstallButton testid="install-banner-button" solid />
    </div>
  );
};
